// ─── MaintenanceHomeView — hub do menu "Manutenção" ───────────────────────
//
// Ecrã de entrada para a área de manutenção: apresenta 4 cards que abrem
// os ecrãs de gestão (Clientes Finais, Postos, Equipamentos, Contratos SLA).
//
// Os cards "Postos" e "Equipamentos" levam para `finalCustomers` — o
// utilizador escolhe a partir daí qual cliente final / posto pretende
// explorar (drilldown). Os outros 2 são ecrãs autónomos.

import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BusinessIcon from '@material-ui/icons/Business';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import BuildIcon from '@material-ui/icons/Build';
import PersonIcon from '@material-ui/icons/Person';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import TimerIcon from '@material-ui/icons/Timer';
import Inventory2Icon from '@material-ui/icons/Category';
import ReceiptIcon from '@material-ui/icons/Receipt';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useDispatch } from 'react-redux';
import { setActiveView, setSelectedFinalCustomer, setSelectedCustomerSite } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import { usePlannerTheme, usePlannerToolbarTheme } from '../../theme/plannerTheme';

// Identidade ecooGPS — só aplicada em Claro/Escuro (Clássico mantém o visual atual).
const BRAND = 'linear-gradient(100deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)';
const BRAND_DARK = 'linear-gradient(100deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', background: 'var(--surf-page, #f8fafc)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '18px 26px', background: 'var(--surf-card, #fff)',
    borderBottom: '1px solid var(--surf-border, #e8edf3)',
  },
  headerIcon: {
    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
    background: 'linear-gradient(135deg, #0369a1, #0ea5e9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
    boxShadow: '0 6px 16px rgba(3,105,161,0.26)',
  },
  title: { fontSize: '1.15rem', fontWeight: 800, color: 'var(--surf-title, #0f172a)', letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.76rem', color: 'var(--surf-muted, #64748b)', marginTop: 1 },
  content: { flex: 1, overflowY: 'auto', padding: 26 },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
    gap: 18,
  },
  card: {
    position: 'relative', background: 'var(--surf-card, #fff)', borderRadius: 16, padding: '20px 20px 16px',
    border: '1px solid var(--surf-border, #e8edf3)', cursor: 'pointer', overflow: 'hidden',
    boxShadow: 'var(--surf-shadow, 0 1px 2px rgba(15,23,42,0.04))',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: 'var(--surf-shadow-lg, 0 14px 30px rgba(15,23,42,0.10))' },
    '&:hover $cardArrow': { opacity: 1, transform: 'translateX(0)' },
  },
  cardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  cardIcon: {
    width: 46, height: 46, borderRadius: 13, display: 'flex',
    alignItems: 'center', justifyContent: 'center', marginBottom: 14,
  },
  cardCountBig: { fontSize: '1.7rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.03em' },
  cardTitle: { fontSize: '0.98rem', fontWeight: 800, color: 'var(--surf-strong, #0f172a)', marginBottom: 4 },
  cardDesc: { fontSize: '0.75rem', color: 'var(--surf-muted, #64748b)', lineHeight: 1.45 },
  cardFooter: {
    marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--surf-border-soft, #f1f5f9)',
    fontSize: '0.64rem', color: 'var(--surf-muted2, #94a3b8)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  cardArrow: {
    display: 'flex', alignItems: 'center', opacity: 0, transform: 'translateX(-4px)',
    transition: 'opacity 0.18s ease, transform 0.18s ease',
  },
}));

function MaintenanceHomeView({ lang = 'pt' }) {
  const classes = useStyles();
  const theme = usePlannerTheme();
  const tbTheme = usePlannerToolbarTheme();
  const newUI = tbTheme !== 'classic'; // identidade ecooGPS só em Claro/Escuro
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const pt = lang === 'pt';
  // Casais (variante mercadoria) → hub mostra tabelas da Casais (Obras, …) em
  // vez das tabelas Petrotec (clientes finais/postos/equipamentos/SLA).
  const isMercadoria = clientSettings?.uiVariant === 'mercadoria';

  const [counts, setCounts] = useState({ customers: null, sites: null, equipments: null, slas: null, stockItems: null, accountingCodes: null, holidays: null, obras: null, transportTypes: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    async function load() {
      setLoading(true);
      try {
        const [fc, cs, se, sla, st, ac, hol, ob, tt] = await Promise.all([
          fetch('/planner-api/final-customers', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/customer-sites', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/site-equipments', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/sla-contracts', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/stock-items', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/accounting-codes', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/holidays-angola', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/obras', { headers }).then(r => r.ok ? r.json() : []),
          fetch('/planner-api/transport-types', { headers }).then(r => r.ok ? r.json() : []),
        ]);
        setCounts({
          customers: Array.isArray(fc) ? fc.length : 0,
          sites: Array.isArray(cs) ? cs.length : 0,
          equipments: Array.isArray(se) ? se.length : 0,
          slas: Array.isArray(sla) ? sla.length : 0,
          stockItems: Array.isArray(st) ? st.length : 0,
          accountingCodes: Array.isArray(ac) ? ac.length : 0,
          holidays: Array.isArray(hol) ? hol.length : 0,
          obras: Array.isArray(ob) ? ob.length : 0,
          transportTypes: Array.isArray(tt) ? tt.length : 0,
        });
      } catch (e) {
        setCounts({ customers: 0, sites: 0, equipments: 0, slas: 0, stockItems: 0, accountingCodes: 0, holidays: 0, obras: 0, transportTypes: 0 });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  // Cards da Casais (variante mercadoria) — migração CasaisHub. Por agora Obras
  // (fundação); Motoristas/Viaturas/Viagens/Dashboard entram nas fases seguintes.
  const casaisCards = [
    {
      key: 'obras', view: 'obras',
      icon: BusinessIcon, color: '#0369a1', bg: '#e0f2fe',
      title: pt ? 'Obras' : 'Works',
      desc: pt
        ? 'Estaleiros/obras (destino das entregas). Código de obra, PEP/CC (SAP), coordenadas e responsável. Migração da CasaisHub.'
        : 'Construction sites (delivery destinations).',
      count: counts.obras,
      footer: pt ? 'Obras registadas' : 'Works',
    },
    {
      key: 'motoristas', view: 'motoristas',
      icon: PersonIcon, color: '#4f46e5', bg: '#eef2ff',
      title: pt ? 'Motoristas' : 'Drivers',
      desc: pt
        ? 'Motoristas do EcooGPS (sync noturno). Define a categoria para filtrar os disponíveis na atribuição.'
        : 'EcooGPS drivers — set category.',
      count: null,
      footer: pt ? 'Categoria por motorista' : 'Category per driver',
    },
    {
      key: 'viaturas', view: 'viaturas',
      icon: LocalShippingIcon, color: '#d97706', bg: '#fef3c7',
      title: pt ? 'Viaturas' : 'Vehicles',
      desc: pt
        ? 'Viaturas do EcooGPS. Define o valor/hora de cada uma para o cálculo de custos dos transportes.'
        : 'EcooGPS vehicles — set hourly rate.',
      count: null,
      footer: pt ? 'Valor/hora por viatura' : 'Hourly rate per vehicle',
    },
    {
      key: 'tiposTransporte', view: 'tiposTransporte',
      icon: Inventory2Icon, color: '#0d9488', bg: '#ccfbf1',
      title: pt ? 'Tipos de Transporte' : 'Transport Types',
      desc: pt
        ? 'Tabela editável dos tipos de transporte (criar/alterar/inativar). Usados nas viagens e no dashboard de custos.'
        : 'Editable transport types table.',
      count: counts.transportTypes,
      footer: pt ? 'Tipos definidos' : 'Types defined',
    },
  ];

  const petrotecCards = [
    {
      key: 'customers', view: 'finalCustomers',
      icon: BusinessIcon, color: '#dc2626', bg: '#fee2e2',
      title: pt ? 'Clientes Finais' : 'Final Customers',
      desc: pt
        ? 'Empresas a quem a Petrotec presta assistência (SONANGOL, TOTAL, ETU Energias…). Ponto de entrada para gerir postos e SLAs.'
        : 'Companies serviced by Petrotec. Entry point for sites and SLAs.',
      count: counts.customers,
      footer: pt ? 'Clientes' : 'Customers',
    },
    {
      key: 'sites', view: 'customerSites',
      icon: LocalGasStationIcon, color: '#0ea5e9', bg: '#e0f2fe',
      title: pt ? 'Postos' : 'Sites',
      desc: pt
        ? 'Locais físicos onde os equipamentos estão instalados (PAs Petrotec). Vê todos do tenant ou filtra por cliente final.'
        : 'Physical sites where equipment is installed. View all or filter by final customer.',
      count: counts.sites,
      footer: pt ? 'Postos no total' : 'Total sites',
    },
    {
      key: 'equipments', view: 'siteEquipments',
      icon: BuildIcon, color: '#f59e0b', bg: '#fef3c7',
      title: pt ? 'Equipamentos' : 'Equipment',
      desc: pt
        ? 'Bombas, tubagens, geradores, AC, sinalética. Identificados por Nº Petrotec e Nº Fabrico. Vê todos ou filtra por posto.'
        : 'Pumps, pipes, generators, HVAC, branding. View all or filter by site.',
      count: counts.equipments,
      footer: pt ? 'Equipamentos no total' : 'Total equipments',
    },
    {
      key: 'stock', view: 'stockItems',
      icon: Inventory2Icon, color: '#3b82f6', bg: '#dbeafe',
      title: pt ? 'Materiais' : 'Materials',
      desc: pt
        ? 'Catálogo central + stock por viatura (cada viatura é um armazém). Reabastecimento, consumo nas OTs e histórico.'
        : 'Central catalogue + stock per vehicle. Replenish, consume in OTs, full history.',
      count: counts.stockItems,
      footer: pt ? 'Materiais no catálogo' : 'Catalogue items',
    },
    {
      key: 'accounting', view: 'accountingCodes',
      icon: ReceiptIcon, color: '#6366f1', bg: '#eef2ff',
      title: pt ? 'Códigos Contabilísticos' : 'Accounting Codes',
      desc: pt
        ? 'Códigos do BM Petrotec (mão obra, km, engenharia) com preço H.N. e H.E. — ponte para o Primavera ERP.'
        : 'BM accounting codes with H.N./H.E. prices — Primavera bridge.',
      count: counts.accountingCodes,
      footer: pt ? 'Códigos definidos' : 'Codes defined',
    },
    {
      key: 'holidays', view: 'holidaysAngola',
      icon: ReceiptIcon, color: '#dc2626', bg: '#fee2e2',
      title: pt ? 'Feriados' : 'Holidays',
      desc: pt
        ? 'Calendário oficial (PT / AO) usado no cálculo H.N./H.E. do BM. Feriados contam sempre como Horas Extra.'
        : 'Official calendar (PT / AO) used in BM H.N./H.E. calculation.',
      count: counts.holidays,
      footer: pt ? 'Feriados configurados' : 'Configured holidays',
    },
    {
      key: 'costsSla', view: 'costsSlaDashboard',
      icon: AssessmentIcon, color: '#16a34a', bg: '#dcfce7',
      title: pt ? 'Custos & SLA' : 'Costs & SLA',
      desc: pt
        ? 'Dashboard de custos por OT (km × preço/km · horas × preço/hora · materiais) + cumprimento de SLAs. Inputs editáveis para simular cenários antes de facturar.'
        : 'Costs per OT + SLA compliance dashboard.',
      count: null,
      footer: pt ? 'Período seleccionável' : 'Period selectable',
    },
    {
      key: 'slas', view: 'slaContracts',
      icon: TimerIcon, color: '#22c55e', bg: '#dcfce7',
      title: pt ? 'Contratos SLA' : 'SLA Contracts',
      desc: pt
        ? 'Horas-alvo por cliente final × tipo de intervenção (acidente, correctiva, preventiva). Determina o relógio de cada OT.'
        : 'Target hours per customer × intervention type. Drives each OT\'s deadline.',
      count: counts.slas,
      footer: pt ? 'SLAs definidos' : 'SLAs defined',
    },
  ];

  const cards = isMercadoria ? casaisCards : petrotecCards;

  function openCard(card) {
    // Limpar drilldown anterior — "Postos"/"Equipamentos" começam sempre num
    // ponto neutro (ecrã de clientes), o utilizador escolhe de quem quer ver.
    dispatch(setSelectedFinalCustomer(null));
    dispatch(setSelectedCustomerSite(null));
    dispatch(setActiveView(card.view));
  }

  return (
    <Box className={classes.root} data-planner-theme={theme}>
      <Box className={classes.header}>
        <Box className={classes.headerIcon}
          style={newUI ? { background: theme === 'dark' ? BRAND_DARK : BRAND, color: '#03121c', boxShadow: '0 8px 20px rgba(24,189,238,0.38)' } : undefined}>
          <BuildIcon />
        </Box>
        <Box>
          <Typography className={classes.title}>
            {pt ? 'Manutenção' : 'Maintenance'}
          </Typography>
          <Typography className={classes.subtitle}>
            {isMercadoria
              ? (pt ? 'Gestão de obras, motoristas, viaturas e transportes' : 'Manage works, drivers, vehicles and transports')
              : (pt
                ? 'Gestão de clientes, postos, equipamentos e SLAs para assistência técnica'
                : 'Manage customers, sites, equipment and SLAs for technical assistance')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.grid}>
          {cards.map(card => {
            const Icon = card.icon;
            return (
              <Box key={card.key} className={classes.card} onClick={() => openCard(card)}>
                <Box className={classes.cardTop}>
                  <Box className={classes.cardIcon} style={{ background: card.bg }}>
                    <Icon style={{ fontSize: 24, color: card.color }} />
                  </Box>
                  {card.count != null && (
                    loading
                      ? <CircularProgress size={16} style={{ color: card.color }} />
                      : <Typography className={classes.cardCountBig} style={{ color: card.color }}>{card.count}</Typography>
                  )}
                </Box>
                <Typography className={classes.cardTitle}>{card.title}</Typography>
                <Typography className={classes.cardDesc}>{card.desc}</Typography>
                <Box className={classes.cardFooter}>
                  <span>{card.footer}</span>
                  <span className={classes.cardArrow} style={{ color: card.color }}>
                    {pt ? 'Abrir' : 'Open'} <ChevronRightIcon style={{ fontSize: 15 }} />
                  </span>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default MaintenanceHomeView;
